import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { loadStoredAuth } from '../authStorage';
import { hasFrontendPermission } from '../permissions';
import { useAppState } from '../state/AppStateContext';
import { useViewportMode } from '../utils/useViewportMode';

const Icons = {
  Dispatch: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  ),
  Jobs: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 4h10M3 8h10M3 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Schedule: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.5" y="2.5" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 1v3M11 1v3M1.5 6.5h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Customers: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Invoices: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="1" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 5h6M5 8h6M5 11h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Projects: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="6" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="1" width="6" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="9" y="8" width="6" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <rect x="1" y="11" width="6" height="4" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  ),
  Planner: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 13.5h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M4.5 11V7.5M8 11V4.5M11.5 11V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="4.5" cy="6.5" r="1" fill="currentColor"/>
      <circle cx="8" cy="3.5" r="1" fill="currentColor"/>
      <circle cx="11.5" cy="5" r="1" fill="currentColor"/>
    </svg>
  ),
  Team: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <circle cx="11.5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M1 14c0-2.761 2.239-4.5 5-4.5s5 1.739 5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M11.5 9.5c1.933 0 3.5 1.343 3.5 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  Inventory: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1L14 4.5v7L8 15 2 11.5v-7L8 1Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M8 1v14M2 4.5l6 3.5 6-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Analytics: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 12l3.5-4 3 2.5L12 5l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Activity: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 12h12M4 10V4m4 6V2m4 8V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Users: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2.5 13.5c0-2.8 2.7-4.5 5.5-4.5s5.5 1.7 5.5 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Settings: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 1.5v1.2M8 13.3v1.2M1.5 8h1.2M13.3 8h1.2M3.4 3.4l.85.85M11.75 11.75l.85.85M3.4 12.6l.85-.85M11.75 4.25l.85-.85" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  Quotes: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 3.5C3 2.67 3.67 2 4.5 2h7c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5h-7C3.67 14 3 13.33 3 12.5v-9Z" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  Dashboard: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M8 8L5 5M8 8l3-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="8" cy="8" r="1" fill="currentColor"/>
    </svg>
  ),
  Chevron: ({ dir = 'left' }) => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ transform: dir === 'right' ? 'rotate(180deg)' : undefined }}>
      <path d="M7.5 2.5L4.5 6l3 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  Close: () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  Logo: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.6"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.6"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.3"/>
    </svg>
  ),
  ExternalLink: () => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
      <path d="M4.5 2H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M6.5 1h3.5v3.5M10 1L5.5 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const APP_NAV = [
  {
    label: 'Coordinate Work',
    items: [
      { to: '/dashboard',       label: 'Overview',    Icon: Icons.Dashboard },
      { to: '/dispatch',        label: 'Dispatch',    Icon: Icons.Dispatch,   permission: 'dispatch.manage' },
      { to: '/jobs',            label: 'Jobs',        Icon: Icons.Jobs,       permission: 'jobs.manage' },
      { to: '/schedule',        label: 'Schedule',    Icon: Icons.Schedule,   permission: 'dispatch.manage' },
    ],
  },
  {
    label: 'Customers & Revenue',
    items: [
      { to: '/customers',       label: 'Customers',  Icon: Icons.Customers, permission: 'customers.manage' },
      { to: '/quotes',          label: 'Quotes',     Icon: Icons.Quotes,    permission: 'quotes.manage' },
      { to: '/invoices',        label: 'Invoices',   Icon: Icons.Invoices,  permission: 'invoices.manage' },
      { to: '/activity',        label: 'Activity',   Icon: Icons.Activity,  permission: 'customers.manage' },
    ],
  },
  {
    label: 'Delivery & Planning',
    items: [
      { to: '/projects',        label: 'Projects',   Icon: Icons.Projects,  permission: 'projects.manage' },
      { to: '/project-planner', label: 'Planner',    Icon: Icons.Planner,   permission: 'projects.manage' },
      { to: '/team',            label: 'Team',       Icon: Icons.Team,      permission: 'technicians.manage' },
      { to: '/inventory',       label: 'Inventory',  Icon: Icons.Inventory, permission: 'inventory.manage' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { to: '/analytics',       label: 'Analytics',  Icon: Icons.Analytics, permission: 'exports.view' },
      { to: '/users',           label: 'Users',      Icon: Icons.Users,     permission: 'accounts.manage' },
      { to: '/settings',        label: 'Settings',   Icon: Icons.Settings,  permission: 'dispatch.manage' },
    ],
  },
];

const visibleItems = (items, user) => items.filter((item) => (
  !item.permission || hasFrontendPermission(user, item.permission)
));

const ROLE_START_LABELS = Object.freeze({
  admin: 'Start here as admin',
  manager: 'Start here as manager',
  dispatcher: 'Start here as dispatcher',
  technician: 'Start here as technician',
});

const getStartItems = (user) => {
  const role = String(user?.role || '').toLowerCase();
  const preferred = role === 'admin'
    ? ['/dashboard', '/dispatch', '/analytics']
    : role === 'manager'
      ? ['/dashboard', '/dispatch', '/projects']
      : role === 'dispatcher'
        ? ['/dispatch', '/jobs', '/schedule']
        : ['/dashboard', '/jobs'];

  const allVisible = APP_NAV.flatMap((section) => visibleItems(section.items, user));
  const byPath = new Map(allVisible.map((item) => [item.to, item]));
  const picks = preferred.map((path) => byPath.get(path)).filter(Boolean);
  if (picks.length > 0) return { label: ROLE_START_LABELS[role] || 'Start here', items: picks };
  return null;
};

const getOrientationCopy = (user) => {
  const role = String(user?.role || '').toLowerCase();
  if (role === 'admin') return 'Monitor the business, then jump into settings or accounts only when needed.';
  if (role === 'manager') return 'Start with operations overview, then balance projects, scheduling, and team load.';
  if (role === 'dispatcher') return 'Open dispatch first, then move through jobs and schedule to keep the field moving.';
  return 'Use the overview to orient yourself, then open the workspace you need.';
};

function NavItem({ to, label, Icon, compact, onClick }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `app-nav-link touch-target${isActive ? ' is-active' : ''}${compact ? ' is-compact' : ''}`
      }
      onClick={onClick}
      aria-label={compact ? label : undefined}
    >
      <span className="app-nav-icon"><Icon /></span>
      <span className="app-nav-label">{label}</span>
    </NavLink>
  );
}

function NavItems({ compact = false, onNavigate = null }) {
  const auth = loadStoredAuth();
  const user = auth?.user;
  const startSection = getStartItems(user);
  const startPaths = new Set((startSection?.items || []).map((item) => item.to));
  const sections = APP_NAV
    .map((section) => ({
      ...section,
      items: visibleItems(section.items, user).filter((item) => !startPaths.has(item.to)),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <ul className="app-nav-list" role="list">
      {startSection && !compact && (
        <li>
          <span className="app-nav-section-label">{startSection.label}</span>
        </li>
      )}
      {startSection?.items.map(({ to, label, Icon }) => (
        <li key={to}>
          <NavItem to={to} label={label} Icon={Icon} compact={compact} onClick={onNavigate} />
        </li>
      ))}

      {sections.map((section) => (
        <React.Fragment key={section.label}>
          {!compact && section.label !== startSection?.label && (
            <li style={{ marginTop: 8 }}>
              <span className="app-nav-section-label">{section.label}</span>
            </li>
          )}
          {section.items.map(({ to, label, Icon }) => (
            <li key={to}>
              <NavItem to={to} label={label} Icon={Icon} compact={compact} onClick={onNavigate} />
            </li>
          ))}
        </React.Fragment>
      ))}
    </ul>
  );
}

function SidebarContent({ compact, isTablet, onNavigate }) {
  const { setTabletRailCollapsed, tabletRailCollapsed } = useAppState();
  const auth = loadStoredAuth();
  const user = auth?.user;
  const orientationCopy = useMemo(() => getOrientationCopy(user), [user]);

  return (
    <>
      <div className="app-nav-header">
        {!compact && (
          <a href="/dispatch" className="sidebar-brand" tabIndex={-1}>
            <span className="sidebar-brand-icon" style={{ color: '#fff' }}>
              <Icons.Logo />
            </span>
            <span className="sidebar-brand-name">FieldOps</span>
          </a>
        )}
        {compact && (
          <span className="sidebar-brand-icon" style={{ color: '#fff', margin: '0 auto' }}>
            <Icons.Logo />
          </span>
        )}
        {isTablet && (
          <button
            type="button"
            className="rail-toggle"
            aria-expanded={!tabletRailCollapsed}
            aria-label={tabletRailCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={() => setTabletRailCollapsed((p) => !p)}
          >
            <Icons.Chevron dir={compact ? 'right' : 'left'} />
          </button>
        )}
      </div>

      <nav role="navigation" aria-label="Main navigation">
        <NavItems compact={compact} onNavigate={onNavigate} />
      </nav>

      {!compact && user ? (
        <div className="sidebar-orientation-card">
          <span className="sidebar-orientation-kicker">Focus</span>
          <strong className="sidebar-orientation-title">
            {String(user.role || 'workspace').replace(/^\w/, (char) => char.toUpperCase())} workspace
          </strong>
          <p className="sidebar-orientation-copy">{orientationCopy}</p>
        </div>
      ) : null}

      {/* Footer: login link */}
      {!compact && (
        <div className="sidebar-footer">
          <a
            href="/login"
            className="app-nav-link"
            style={{ fontSize: 12, color: 'var(--text-muted)' }}
          >
            <span className="app-nav-icon"><Icons.ExternalLink /></span>
            <span className="app-nav-label">Login / Switch account</span>
          </a>
        </div>
      )}
    </>
  );
}

export function AdaptiveNavigation() {
  const { mobileNavOpen, setMobileNavOpen, tabletRailCollapsed } = useAppState();
  const mode = useViewportMode();
  const isMobile = mode === 'mobile';
  const isTablet = mode === 'tablet';
  const compact = isTablet && tabletRailCollapsed;

  if (isMobile) {
    return (
      <>
        <div
          className={`app-mobile-backdrop${mobileNavOpen ? ' is-open' : ''}`}
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
        <aside
          className={`app-mobile-drawer${mobileNavOpen ? ' is-open' : ''}`}
          aria-label="Primary navigation"
        >
          <SidebarContent
            compact={false}
            isTablet={false}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </aside>
      </>
    );
  }

  return (
    <aside
      className={`app-sidebar${compact ? ' is-rail' : ''}`}
      aria-label="Primary navigation"
    >
      <SidebarContent compact={compact} isTablet={isTablet} onNavigate={null} />
    </aside>
  );
}
